import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { EMPTY, from, Observable, throwError } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import { SessionStorageKeys } from 'src/app/shared/constants/session-storage-keys'
import { StorageKeys } from 'src/app/shared/constants/storage-keys'
import { retrieveAndSetLanguage } from 'src/app/shared/helpers/language'
import { KtechHttpResponse, KtechHttpResponseStatus } from 'src/app/shared/models/ktech-http-response'
import { AuthorizationPrefixes, ByPassAuthTokenPrefixes } from 'src/app/shared/models/other-module'
import { RoleDepartment } from 'src/app/shared/models/user-info'
import { environment } from 'src/environments/environment'

import { AuthService } from '../auth/auth.service'
import { LocalStorageService } from '../storage/local-storage.service'
import { SessionStorageService } from '../storage/session-storage.service'
import { UtilitiesService } from '../utilities/utilities.service'

@Injectable()
export class InterceptorService implements HttpInterceptor {
  constructor(
    private readonly authService: AuthService,
    private readonly localStorage: LocalStorageService,
    private readonly sessionStorage: SessionStorageService,
    public readonly router: Router,
    private readonly utilitiesService: UtilitiesService,
    private readonly translate: TranslateService
  ) {
    translate.setDefaultLang('en-US')
    const translationOverrideValue = this.localStorage.get(StorageKeys.translationOverride)
    retrieveAndSetLanguage('en-US', this.translate, translationOverrideValue)

    this.translate.onLangChange.subscribe(language => {
      retrieveAndSetLanguage(language.lang, this.translate, translationOverrideValue)
    })
  }
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let headers = req.headers.set('subdomain', environment.subdomain)
    if (AuthorizationPrefixes.some(prefix => req.url.startsWith(prefix))) {
      // identity.getToken() covers Dex; authService.keycloak is undefined there.
      headers = headers.set('Authorization', `Bearer ${this.authService.identity?.getToken() ?? ''}`)
    }
    // Blank when the realm matches the tenant; OIDCGuard reads this with ?? so an empty value beats its fallback.
    const authSubdomain = environment.keycloakRealm || environment.subdomain
    if (authSubdomain) {
      headers = headers.set('auth-subdomain', authSubdomain)
    }
    const origin = this.sessionStorage.get(SessionStorageKeys.origin) as string

    const selectedLanguage: any = this.sessionStorage.get(SessionStorageKeys.selectedLanguage)
    const selectedLanguageId = selectedLanguage?.id
    const authToken = this.localStorage.get(StorageKeys.authToken) as string

    // Only guard on missing auth-token when the tenant actually owns the session
    // app-side; provider-owned tenants authenticate via Bearer alone.
    if (
      environment.appSessionsEnabled &&
      !authToken &&
      !ByPassAuthTokenPrefixes.some(prefix => req.url.startsWith(prefix))
    ) {
      this.utilitiesService.errorHandlerMessage(this.translate.instant('ERROR_CODE.SESSION_EXPIRED'))
      this.logout()
      return EMPTY
    }
    if (authToken) headers = headers.set('Auth-Token', String(authToken))

    if (origin) {
      headers = headers.set('originpath', String(origin))
    }

    const selectedDept: RoleDepartment | string = this.localStorage.get(
      StorageKeys.selectedDepartment
    ) as RoleDepartment
    if (selectedDept && selectedDept?.id && selectedDept?.id != '') headers = headers.set('department', selectedDept.id)

    if (selectedLanguageId) {
      headers = headers.set('language', String(selectedLanguageId))
    }

    const dupReq = req.clone({
      headers: headers
    })
    return next.handle(dupReq).pipe(
      map((event: HttpEvent<any>) => this.mapHttpResult(event)),
      catchError(err => {
        if (err?.error?.errorCode && err.status != HttpStatusCodes.Unauthorized) {
          if (err?.error?.errorCode == '4048') {
            this.authService.setUserInfoMessage('ClientExpired')
          } else if (err?.error?.errorCode == '4050') {
            this.authService.setUserInfoMessage('UserLimitExceeded')
          } else if (err?.error?.errorCode == '4045') {
            this.authService.setUserInfoMessage('UserCreationLimitExceed')
          } else if (err?.error?.errorCode == '4101') {
            this.authService.setUserInfoMessage('UserAccountDisabled')
          } else if (err?.error?.errorCode == '4207' && err?.error?.message) {
            // The answer-key rejection names the offending question numbers, which is the only
            // part that tells the author what to fix. A generic translated string would lose it.
            this.utilitiesService.errorHandlerMessage(err.error.message)
          } else {
            this.utilitiesService.errorHandlerMessage(this.translate.instant('ERROR_CODE.' + err.error.errorCode))
          }
        }
        switch (err.status) {
          case HttpStatusCodes.UserNotFound:
            if (HttpStatusCodes.UserNotFound == err.error.status) {
              this.utilitiesService.errorHandlerMessage(err.error.message)
            }
            break
          case HttpStatusCodes.Unauthorized:
            if (HttpStatusCodes.Unauthorized == 401) {
              return from(this.authService.identity.refreshToken(30)).pipe(
                switchMap(() => {
                  const refreshedClone = req.clone({
                    headers: headers.set('Authorization', `Bearer ${this.authService.identity.getToken() as string}`)
                  })
                  return next.handle(refreshedClone).pipe(map((event: HttpEvent<any>) => this.mapHttpResult(event)))
                }),
                catchError(() => {
                  // Retry also 401 — app-session is gone. Clear state, end provider
                  // session, hard-fallback if that throws so the dashboard can't wedge.
                  this.sessionStorage.clearAll()
                  this.localStorage.clearAll()
                  const target = window.location.origin + window.location.pathname
                  try {
                    const result = this.authService?.identity.logout(target)
                    if (result && typeof (result as Promise<unknown>).catch === 'function') {
                      ;(result as Promise<unknown>).catch(() => (window.location.href = target))
                    }
                  } catch {
                    window.location.href = target
                  }
                  return EMPTY
                })
              )
            }
            break
          case HttpStatusCodes.LimitExceeded:
          case HttpStatusCodes.ContentNotAvailable:
            if (err.error && err.error.messages && err.error.messages.length) {
              this.utilitiesService.errorHandlerMessage(err.error.messages.join(','))
            }
            break
        }

        return throwError(err)
      })
    )
  }
  private mapHttpResult(event: HttpEvent<any>) {
    if (event instanceof HttpResponse) {
      if (event.url.indexOf(environment.apiBase) === -1) {
        return event
      }
      const _res = event.body as KtechHttpResponse
      if (_res.statusCode === KtechHttpResponseStatus.OK && (_res.result !== undefined || _res.result !== null)) {
        const _event = event.clone({
          body: _res.result
        })
        return _event
      } else if (_res !== undefined || _res !== null) {
        const _event = event.clone({
          body: _res
        })
        return _event
      } else {
        const error = { status: _res.statusCode, error: _res.error }
        throw error
      }
    }
    return event
  }

  private async logout() {
    this.sessionStorage.clearAll()
    await this.authService?.identity.logout(window.location.origin + window.location.pathname)
  }
}

export enum HttpStatusCodes {
  Unauthorized = 401,
  ContentNotAvailable = 410,
  UserNotFound = 422,
  LimitExceeded = 429,
  Badrequest = 400,
  ValidationError = 400
}
